import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  IconButton,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useLayoutPreferences } from '../../../../context/LayoutPreferencesContext';
import useGetPersonalFields, { PersonalField } from '../../../../hooks/Marketing/useGetPersonalFields';

interface InsertPersonalFieldsDialogProps {
  open: boolean;
  onClose: () => void;
  onInsert: (token: string) => void;
  channel: 'email' | 'sms' | 'cashdesk';
}

const DIALOG_TITLE: Record<string, string> = {
  email: 'Insertion de champs personnalisés',
  sms: 'Champs de Personnalisation',
  cashdesk: 'Champs de Personnalisation',
};

const API_CHANNEL: Record<string, 'EMAILING' | 'SMS' | 'CASHDESK'> = {
  email: 'EMAILING',
  sms: 'SMS',
  cashdesk: 'CASHDESK',
};

function InsertPersonalFieldsDialog({ open, onClose, onInsert, channel }: InsertPersonalFieldsDialogProps) {
  const { sidebarColor } = useLayoutPreferences();
  const { data: categories, loading, error } = useGetPersonalFields(API_CHANNEL[channel]);

  const categoryNames = Object.keys(categories);
  const [selectedCategory, setSelectedCategory] = useState('');

  useEffect(() => {
    if (categoryNames.length > 0 && !selectedCategory) {
      setSelectedCategory(categoryNames[0]);
    }
  }, [categoryNames, selectedCategory]);

  useEffect(() => {
    if (!open) setSelectedCategory('');
  }, [open]);

  const fieldsInCategory: PersonalField[] = selectedCategory ? (categories[selectedCategory] ?? []) : [];

  const handleInsert = (anchor: string) => {
    onInsert(anchor);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2.5, boxShadow: '0 8px 32px rgba(0,0,0,0.12)' } }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 1.75,
          px: 3,
          backgroundColor: sidebarColor,
        }}
      >
        <Typography variant="subtitle1" fontWeight={600} sx={{ color: '#fff', fontSize: '0.95rem' }}>
          {DIALOG_TITLE[channel]}
        </Typography>
        <IconButton size="small" onClick={onClose} sx={{ color: '#fff', opacity: 0.85, '&:hover': { opacity: 1 } }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, '&.MuiDialogContent-root': { paddingTop: '24px' } }}>
        {channel === 'sms' && (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, p: 1.5, mb: 2.5, borderRadius: 1.5, backgroundColor: '#e8f4fd', border: '1px solid #b3d9f7' }}>
            <InfoOutlinedIcon sx={{ fontSize: '1rem', color: '#1976d2', mt: 0.15, flexShrink: 0 }} />
            <Typography variant="body2" sx={{ fontSize: '0.82rem', color: '#1565c0', lineHeight: 1.5 }}>
              Le nombre de SMS envoyés peut varier en fonction de l&apos;utilisation de champs de personnalisation.
            </Typography>
          </Box>
        )}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} sx={{ color: sidebarColor }} />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2, fontSize: '0.82rem', borderRadius: 1.5 }}>{error}</Alert>
        )}

        {!loading && !error && (
          <>
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel sx={{ '&.Mui-focused': { color: sidebarColor } }}>
                Sélectionnez une catégorie
              </InputLabel>
              <Select
                value={selectedCategory}
                label="Sélectionnez une catégorie"
                onChange={e => setSelectedCategory(e.target.value)}
                MenuProps={{ PaperProps: { sx: { maxHeight: 240 } } }}
                sx={{
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: sidebarColor },
                  borderRadius: 1.5,
                }}
              >
                {categoryNames.map(cat => (
                  <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedCategory && fieldsInCategory.length > 0 && (
              <>
                <Typography variant="caption" sx={{ display: 'block', mb: 1, color: '#6c757d', fontSize: '0.72rem', letterSpacing: 0.4, textTransform: 'uppercase', fontWeight: 600 }}>
                  Champs disponibles
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                  {fieldsInCategory.map(f => (
                    <Button
                      key={f.ANCHOR}
                      variant="outlined"
                      size="small"
                      onClick={() => handleInsert(f.ANCHOR)}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 500,
                        fontSize: '0.8rem',
                        borderRadius: 5,
                        borderColor: `${sidebarColor}50`,
                        color: '#2c3e50',
                        backgroundColor: '#fff',
                        px: 1.5,
                        py: 0.4,
                        '&:hover': {
                          borderColor: sidebarColor,
                          backgroundColor: `${sidebarColor}10`,
                          color: sidebarColor,
                        },
                      }}
                    >
                      {f.NAME}
                    </Button>
                  ))}
                </Box>
              </>
            )}

            {selectedCategory && fieldsInCategory.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', fontSize: '0.82rem' }}>
                Aucun champ disponible dans cette catégorie.
              </Typography>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ borderTop: '1px solid #e9ecef', px: 3, py: 1.5 }}>
        <Button
          variant="outlined"
          startIcon={<CloseIcon fontSize="small" />}
          onClick={onClose}
          sx={{ textTransform: 'none', fontSize: '0.82rem', color: '#6c757d', borderColor: '#dee2e6', borderRadius: 1.5, '&:hover': { borderColor: '#adb5bd', backgroundColor: '#f8f9fa' } }}
        >
          Fermer
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default InsertPersonalFieldsDialog;
